import { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { matchPath, withRouter } from 'react-router';
import qs from 'qs';

import { api, getCustomerAnalytics, restoreAnalytics, serviceApi } from '~/api';
import { triggerErrorAnalytics, triggerPageLoadAnalytics } from '~/analytics/analytics.listener';
import {
  isSecuredPath,
  getAppointmentDetailsReferrer,
  updateAppointmentsDetailsInWindowDataLayer,
} from '~/analytics/analytics.utils';
import { getDisplayName } from '~/utils';
import { reduxFormSelectors } from '~/utils/reduxForm.selectors';

import { getErrorMessage } from '../messages/messages.constants';
import { getTokenFromStorage } from '../persist-token/persist-token.utils';
import { apiCallFinished } from '../loading-spinner';
import { envConfigSelectors } from '~/components';

const resolveErrorMessage = ({ type, description, title }) =>
  description || title || getErrorMessage(type).title;

export const withAnalytics = (Component) =>
  class AnalyticHoc extends PureComponent {
    static propTypes = {
      location: PropTypes.shape({
        pathname: PropTypes.string.isRequired,
      }).isRequired,
      errorMessages: PropTypes.arrayOf(PropTypes.shape({})),
      shopId: PropTypes.string,
      appointmentType: PropTypes.shape({}),
      getAppointmentsDetails: PropTypes.func,
      questionsForm: PropTypes.shape({}),
      dispatchRestoreAnalytics: PropTypes.func.isRequired,
      dispatchApiCallFinished: PropTypes.func.isRequired,
      isServiceWithoutShops: PropTypes.bool,
      isRecommendedByAFriend: PropTypes.bool,
    };

    static defaultProps = {
      errorMessages: [],
      shopId: null,
      appointmentType: null,
      getAppointmentsDetails: null,
      questionsForm: null,
      isServiceWithoutShops: false,
    };

    static displayName = `WithAnalytics(${getDisplayName(Component)})`;

    static isPageRefreshed = (prevPath, shopIdSelected, pathWithBasename) =>
      !prevPath &&
      shopIdSelected &&
      pathWithBasename !== `${api.BASENAME}/shops` &&
      pathWithBasename !== `${api.BASENAME}/fitting-type` &&
      pathWithBasename !== `${api.BASENAME}/treatment-category` &&
      pathWithBasename !== `${api.BASENAME}/`;

    static getBookingId = () => {
      const match = matchPath(window.location.pathname, {
        path: `${api.BASENAME}/bookings/:id`,
      });
      return match ? match.params.id : null;
    };

    static sessionCamAction = (currentPath, bookingId) => {
      if (window.sessionCamRecorder && window.sessionCamRecorder.createVirtualPageLoad) {
        window.sessionCamRecorder.createVirtualPageLoad(
          currentPath.replace(new RegExp(`/${bookingId}.*$`), ''),
        );
      }
    };

    state = {
      prevPath: null,
      isAnalyticsRestored: false,
    };

    static getDerivedStateFromProps(props, state) {
      const {
        location: { pathname: path },
        errorMessages,
        shopId,
        appointmentType,
        getAppointmentsDetails,
        questionsForm,
        isRecommendedByAFriend,
        isServiceWithoutShops,
      } = props;
      const currentPath = `${api.BASENAME}${path}`;
      const { prevPath } = state;
      const errorMessage = errorMessages[0];

      if (AnalyticHoc.isPageRefreshed(prevPath, !isServiceWithoutShops && !shopId, currentPath)) {
        return null;
      }

      if (prevPath !== currentPath) {
        const bookingId = AnalyticHoc.getBookingId();
        AnalyticHoc.sessionCamAction(currentPath, bookingId);
        if (isSecuredPath(currentPath)) {
          getCustomerAnalytics().then((customerData) => {
            if (bookingId) {
              triggerPageLoadAnalytics(
                currentPath,
                shopId,
                {
                  ...(getAppointmentsDetails ? getAppointmentsDetails(appointmentType) : {}),
                  ...getAppointmentDetailsReferrer(isRecommendedByAFriend, questionsForm),
                },
                customerData,
              );
            } else {
              triggerPageLoadAnalytics(
                currentPath,
                shopId,
                getAppointmentsDetails && getAppointmentsDetails(appointmentType),
                customerData,
              );
            }
          });
        } else {
          triggerPageLoadAnalytics(
            currentPath,
            shopId,
            getAppointmentsDetails && getAppointmentsDetails(appointmentType),
          );
        }
      } else if (errorMessage && (!errorMessage.status || errorMessage.status < 500)) {
        triggerErrorAnalytics(currentPath, shopId, resolveErrorMessage(errorMessage));
      }

      return {
        ...state,
        prevPath: currentPath,
      };
    }

    componentDidMount() {
      this.restoreAnalytics();
    }

    componentDidUpdate(prevProps) {
      const { appointmentType, getAppointmentsDetails } = this.props;
      const { appointmentType: prevAppointmentType } = prevProps;

      this.restoreAnalytics();

      const isAppointmentTypeNameLoaded =
        appointmentType &&
        appointmentType.name &&
        appointmentType.name !== prevAppointmentType.name;
      const shouldTrackDetails = !!getAppointmentsDetails;

      if (shouldTrackDetails && isAppointmentTypeNameLoaded) {
        const { journey } = getAppointmentsDetails(appointmentType);

        updateAppointmentsDetailsInWindowDataLayer({ journey });
      }
    }

    restoreAnalytics = () => {
      const { dispatchRestoreAnalytics } = this.props;
      const { isAnalyticsRestored } = this.state;

      const token = getTokenFromStorage();
      if (token && !isAnalyticsRestored) {
        serviceApi
          .fetchJSON(`analytics?${qs.stringify({ token }, { skipNulls: true })}`)
          .then((result) => {
            dispatchRestoreAnalytics(result);
          })
          .finally(() => {
            this.setState({ isAnalyticsRestored: true });
          });
      }
    };

    render() {
      return <Component {...this.props} />;
    }
  };

export const mapStateToProps = (state) => {
  const {
    messages: {
      app: { errorMessages },
    },
    services: {
      config: { getAppointmentsDetails },
    },
    appointment: {
      draft: {
        appointmentType,
        shop: { id: shopId },
      },
    },
  } = state;

  return {
    errorMessages,
    shopId,
    appointmentType,
    getAppointmentsDetails,
    questionsForm: reduxFormSelectors.selectFormValues(state),
    isRecommendedByAFriend: reduxFormSelectors.selectIsRecommendedByAFriend(state),
    isServiceWithoutShops: envConfigSelectors.selectIsServiceWithoutShops(state),
  };
};

export const mapDispatchToProps = {
  dispatchRestoreAnalytics: restoreAnalytics,
  dispatchApiCallFinished: apiCallFinished,
};

const ConnectedWithAnalytics = (Component) =>
  connect(mapStateToProps, mapDispatchToProps)(withAnalytics(Component));

export const ConnectedWithAnalyticsRouter = (Component) =>
  withRouter(ConnectedWithAnalytics(Component));
